import React, { useState, useEffect } from 'react';
import ApiClient from '../ApiClient';
import { notify } from '../utils/ToastUtils';
import moment from 'moment';
import CentralizedTable from '../components/CentralizedTable';
import LoaderIcon from '../components/LoaderIcon';
import ActionConfirmModal from '../components/ActionConfirmModal';
import { CalendarDays, Trash2, Plus, Edit2, Search, Filter, List, Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, Sparkles, Upload, Download, Info } from 'lucide-react';
import { IconButton, Tooltip, TextField, Select, MenuItem, InputAdornment } from '@mui/material';
import { LOCATIONS } from '../utils/constants';

const HOLIDAY_COLORS = [
    { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', month: 'text-red-600' },
    { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', month: 'text-blue-600' },
    { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', month: 'text-green-600' },
    { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', month: 'text-purple-600' },
    { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', month: 'text-orange-600' },
    { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200', month: 'text-pink-600' },
    { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', month: 'text-teal-600' },
    { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', month: 'text-indigo-600' },
    { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', month: 'text-rose-600' },
    { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', month: 'text-amber-600' },
    { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', month: 'text-cyan-600' },
    { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', month: 'text-emerald-600' },
    { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200', month: 'text-fuchsia-600' },
];

let colorIndex = 0;
const occasionColorMap = {};

const getHolidayColor = (occasion) => {
    if (!occasion) return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200', month: 'text-slate-500' };
    
    if (!occasionColorMap[occasion]) {
        occasionColorMap[occasion] = HOLIDAY_COLORS[colorIndex % HOLIDAY_COLORS.length];
        colorIndex++;
    }
    
    return occasionColorMap[occasion];
};

function OthersSA() {
    const currentYear = moment().format("YYYY");
    const [currentActiveTab, setCurrentActiveTab] = useState('1'); // 1 = India, 2 = US
    const [indiaHolidays, setIndiaHolidays] = useState([]);
    const [usHolidays, setUsHolidays] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [filteredHolidays, setFilteredHolidays] = useState([]);
    const [disableHoliday, setDisableHoliday] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    const [currentDate, setCurrentDate] = useState(moment());
    const [selectedDate, setSelectedDate] = useState(moment());
    const [errors, setErrors] = useState({});
    
    // Delete Modal State
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const initialState = {
        id: null,
        occasion: "",
        date: moment().format("YYYY-MM-DD"),
        location: "India"
    };
    
    const [state, setState] = useState(initialState);

    function handleChange(evt) {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value
        }); 
        
        if (errors[evt.target.name]) {
            setErrors(prev => ({ ...prev, [evt.target.name]: null }));
        }
        if (evt.target.name === 'date') {
            setSelectedDate(moment(value));
            setCurrentDate(moment(value));
        }
    }

    const resetForm = () => {
        setState({
            id: null,
            occasion: "",
            date: selectedDate.format("YYYY-MM-DD"),
            location: "India"
        });
        setErrors({});
    }

    const handleSaveHoliday = async (e) => {
        e.preventDefault();
        
        let newErrors = {};
        if (!state.occasion) newErrors.occasion = "Please fill in this field.";
        if (!state.location) newErrors.location = "Please select a location.";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (state.occasion && state.date && state.location) {
            setDisableHoliday(true);
            try {
                if (state.id) {
                    await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/admin/editOfficeHoliday`, {
                        id: state.id,
                        occasion: state.occasion,
                        date: state.date,
                        location: state.location,
                        day: moment(state.date).format("dddd"),
                        year: moment(state.date).format("YYYY"),
                    });
                    notify.success("Holiday updated successfully");
                } else {
                    await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/addOfficeHoliday`, {
                        occasion: state.occasion,
                        date: state.date,
                        location: state.location,
                        day: moment(state.date).format("dddd"),
                        year: moment(state.date).format("YYYY"),
                    });
                    notify.success("Holiday added successfully");
                }
                resetForm();
                setDisableHoliday(false);
                getOfficeHolidays();
            } catch (error) {
                console.log(error);
                setDisableHoliday(false);
                let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
                notify.error(errmsg);
            }
        }
    };

    const getOfficeHolidays = async () => {
        setIsLoading(true);
        try {
            const { data } = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/officeholiday`, {
                year: currentYear
            });
            
            let indiaData = [];
            let usData = [];

            let sortedData = data.sort((a, b) => new Date(a["DATE"]) - new Date(b["DATE"]));

            sortedData.forEach((el, index) => {
                el.id = index + 1;
                if (el.LOCATION === "India") indiaData.push(el);
                else usData.push(el); // anything else goes to US for now
            });

            setIndiaHolidays(indiaData);
            setUsHolidays(usData);
            setHolidays(sortedData);
        } catch (error) {
            console.log(error);
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
            notify.error(errmsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (holiday) => {
        setState({
            id: holiday.HOLIDAY_ID,
            occasion: holiday.OCCASION,
            date: moment(holiday.DATE).format("YYYY-MM-DD"),
            location: holiday.LOCATION
        });
        const d = moment(holiday.DATE);
        setSelectedDate(d);
        setCurrentDate(d);
        const formElement = document.getElementById('holiday-form-section');
        if (formElement) {
            // Slight delay ensures state has settled before scrolling
            setTimeout(() => {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    const handleDeleteClick = (holiday) => {
        setHolidayToDelete(holiday);
        setIsDeleteModalOpen(true);
    };

    const deleteHoliday = async () => {
        if (!holidayToDelete) return;
        
        setIsDeleting(true);
        try {
            await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/leave/removeHoliday`, {
                data: { id: holidayToDelete.HOLIDAY_ID }
            });
            notify.success("Holiday removed successfully");
            getOfficeHolidays();
            if (state.id === holidayToDelete.HOLIDAY_ID) {
                resetForm();
            }
            setIsDeleteModalOpen(false);
            setHolidayToDelete(null);
        } catch (error) {
            console.log(error);
            let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later";
            notify.error(errmsg);
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        getOfficeHolidays();
    }, []);

    useEffect(() => {
        const activeHolidays = currentActiveTab === '1' ? indiaHolidays : usHolidays;
        if (searchQuery) {
            const lowercasedQuery = searchQuery.toLowerCase();
            const filtered = activeHolidays.filter(h => 
                h.OCCASION.toLowerCase().includes(lowercasedQuery) ||
                moment(h.DATE).format("DD-MMM-YYYY").toLowerCase().includes(lowercasedQuery) ||
                h.LOCATION.toLowerCase().includes(lowercasedQuery)
            );
        } else {
            setFilteredHolidays(activeHolidays);
        }
    }, [indiaHolidays, usHolidays, searchQuery, currentActiveTab]);

    // Calendar Logic
    const generateCalendar = () => {
        const startDay = currentDate.clone().startOf('month').startOf('isoWeek');
        const endDay = currentDate.clone().endOf('month').endOf('isoWeek');
        const day = startDay.clone().subtract(1, 'day');
        const calendar = [];
        while (day.isBefore(endDay, 'day')) {
            calendar.push(
                Array(7).fill(0).map(() => day.add(1, 'day').clone())
            );
        }
        return calendar;
    };

    const calendar = generateCalendar();
    
    const getHoliday = (date, location) => {
        const list = location === 'India' ? indiaHolidays : usHolidays;
        return list.find(h => moment(h.DATE).isSame(date, 'day'));
    };



    const handleDateClick = (day) => {
        setSelectedDate(day);
        setState({
            ...state,
            date: day.format("YYYY-MM-DD")
        });
    };

    const columns = [
        { 
            headerName: "Date", 
            field: "DATE", 
            sortable: true, 
            flex: 0.6,
            minWidth: 120,
            renderCell: (params) => {
                const holColor = getHolidayColor(params.row?.OCCASION);
                return (
                    <div className="flex items-center gap-4">
                        <div className={`flex flex-col items-center justify-center w-8 h-8 rounded-xl border text-center leading-none shrink-0 ${holColor.bg} ${holColor.border}`}>
                            <span className={`text-[12px] font-bold mb-[1px] ${holColor.text}`}>{moment(params.row?.DATE).format("DD")}</span>
                            <span className={`text-[8px] font-bold uppercase ${holColor.month}`}>{moment(params.row?.DATE).format("MMM")}</span>
                        </div>
                        <span className="text-[13px] font-semibold text-slate-700">{moment(params.row?.DATE).format("dddd")}</span>
                    </div>
                );
            }
        },
        {
            headerName: "Occasion",
            field: "OCCASION",
            sortable: true,
            flex: 1, 
        },
        {
            headerName: "Location",
            field: "LOCATION",
            sortable: true,
            width: 150, 
        },
        {
            headerName: "Action",
            field: "HOLIDAY_ID",
            sortable: false,
            width: 100,
            headerAlign: 'center',
            renderCell: (params) => (
                <div className="flex items-center gap-2">
                    <Tooltip title="Edit Holiday">
                        <button onClick={() => handleEditClick(params.row)} className="w-7 h-7 rounded border border-blue-100 text-blue-500 flex items-center justify-center hover:bg-blue-50 transition-colors bg-white shadow-sm">
                            <Edit2 size={12} strokeWidth={2.5} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Delete Holiday">
                        <button onClick={() => handleDeleteClick(params.row)} className="w-7 h-7 rounded border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors bg-white shadow-sm">
                            <Trash2 size={12} strokeWidth={2.5} />
                        </button>
                    </Tooltip>
                </div>
            ) 
        }
    ];

    return (
        <div className="flex flex-col w-full space-y-3 min-h-screen rounded-xl">
            {/* Single Card for Top 3 elements */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col space-y-3">
                
                {/* Top Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                            <CalendarDays size={20} />
                        </div>
                        <div>
                        <h2 className="text-lg font-bold text-slate-800 m-0 leading-tight">
                        Manage Office Holidays
                        </h2>
                        <p className="text-xs text-slate-500 font-medium mt-0.5 block m-0">
                        Create, update, and manage holidays across office locations.
                        </p>
                        </div>
                    </div>
                </div>

                {/* Middle Section: Grid for Calendar and Form */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                    
                    {/* Left Panel: Calendar */}
                    <div className="lg:col-span-5 xl:col-span-4 rounded-xl border border-slate-200 p-3 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <IconButton onClick={() => setCurrentDate(currentDate.clone().subtract(1, 'month'))} size="small" className="text-slate-600 hover:bg-slate-50">
                            <ChevronLeft size={20} />
                        </IconButton>
                        
                        <div className="flex items-center gap-2">
                            <Select
                                size="small"
                                value={currentDate.month()}
                                onChange={(e) => setCurrentDate(currentDate.clone().month(e.target.value))}
                                className="bg-white text-sm font-bold text-slate-800"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e2e8f0',
                                        borderRadius: '0.5rem',
                                    },
                                    '& .MuiSelect-select': {
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        paddingLeft: '12px',
                                        color: '#1e293b'
                                    },
                                    height: '36px',
                                    width: '110px'
                                }}
                            >
                                {moment.months().map((month, idx) => (
                                    <MenuItem key={month} value={idx} sx={{ fontSize: '14px', fontWeight: 500 }}>
                                        {month}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Select
                                size="small"
                                value={currentDate.year()}
                                onChange={(e) => setCurrentDate(currentDate.clone().year(e.target.value))}
                                className="bg-white text-sm font-bold text-slate-800"
                                sx={{
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#e2e8f0',
                                        borderRadius: '0.5rem',
                                    },
                                    '& .MuiSelect-select': {
                                        paddingTop: '6px',
                                        paddingBottom: '6px',
                                        paddingLeft: '12px',
                                        color: '#1e293b'
                                    },
                                    height: '36px',
                                    width: '90px'
                                }}
                            >
                                {Array.from({ length: 21 }, (_, i) => parseInt(moment().format("YYYY")) - 10 + i).map(year => (
                                    <MenuItem key={year} value={year} sx={{ fontSize: '14px', fontWeight: 500 }}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>

                        <IconButton onClick={() => setCurrentDate(currentDate.clone().add(1, 'month'))} size="small" className="text-slate-600 hover:bg-slate-50">
                            <ChevronRight size={20} />
                        </IconButton>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                            <div key={d} className="text-center text-xs font-medium text-slate-500 py-1">
                                {d}
                            </div>
                        ))}
                    </div>

                    <div className="flex-1 flex flex-col gap-0">
                        {calendar.map((week, i) => (
                            <div key={i} className="grid grid-cols-7 gap-1">
                                {week.map((day, j) => {
                                    const isCurrentMonth = day.isSame(currentDate, 'month');
                                    const isToday = day.isSame(moment(), 'day');
                                    const isSelected = day.isSame(selectedDate, 'day');
                                    const isIndiaHol = getHoliday(day, 'India');
                                    const isUsHol = getHoliday(day, 'US');
                                    const activeHol = currentActiveTab === '1' ? isIndiaHol : isUsHol;
                                    const holColor = activeHol ? getHolidayColor(activeHol.OCCASION) : null;
                                    
                                    let buttonClass = "h-8 w-8 flex items-center justify-center rounded-full text-sm font-semibold transition-all ";
                                    
                                    if (isSelected) {
                                        buttonClass += "bg-blue-600 text-white shadow-md";
                                    } else if (activeHol) {
                                        buttonClass += `${holColor.bg} ${holColor.text} border ${holColor.border}`;
                                    } else if (!isCurrentMonth) {
                                        buttonClass += "text-slate-300 hover:bg-slate-50";
                                    } else {
                                        buttonClass += "text-slate-700 hover:bg-slate-100";
                                    }

                                    return (
                                        <div key={j} className="flex flex-col items-center justify-start h-12 cursor-pointer" onClick={() => handleDateClick(day)}>
                                            <div className={buttonClass} title={activeHol ? activeHol.OCCASION : undefined}>
                                                {day.format("D")}
                                            </div>
                                            <div className="flex gap-1 mt-1">
                                                {isToday && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" title="Today"></div>}
                                                {isIndiaHol && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="India Holiday"></span>}
                                                {isUsHol && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="US Holiday"></span>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div className="mt-1 flex flex-col items-center justify-center gap-2 pb-2 border-t border-slate-100 pt-4">
                        <div className="text-[11px] font-medium text-slate-500 text-center mb-1">
                            Match calendar colors with the table to easily identify holidays
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                <span className="text-[11px] font-bold text-slate-700">Today</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                <span className="text-[11px] font-bold text-slate-700">India Holiday</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                <span className="text-[11px] font-bold text-slate-700">US Holiday</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel: Holiday Details */}
                <div id="holiday-form-section" className="lg:col-span-7 xl:col-span-8 rounded-xl border border-slate-200 p-3 flex flex-col">
                    <div className="flex items-center gap-3 pb-3 border-b border-slate-100 pb-2">
                        <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                            <CalendarDays size={20} />
                        </div>
                        <h2 className="text-lg font-bold text-slate-800 m-0 leading-tight">{state.id ? 'Edit Holiday' : 'Add Holiday'}</h2>
                    </div>

                    <form onSubmit={handleSaveHoliday} className="flex-1 flex flex-col my-5" noValidate>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Date (Selected from Calendar)</label>
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        value={moment(state.date).format("DD-MMM-YYYY")}
                                        readOnly
                                        className="w-full bg-slate-50 border border-slate-200 text-slate-500 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-3 py-2 pl-10 outline-none cursor-not-allowed"
                                    />
                                    <CalendarIcon size={18} className="absolute left-3 top-2.5 text-slate-400" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Location <span className="text-red-500">*</span></label>
                                    <Select 
                                        size="small" 
                                        name="location" 
                                        value={state.location} 
                                        onChange={handleChange} 
                                        className="w-full bg-white text-sm" 
                                        error={!!errors.location}
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderRadius: '0.5rem',
                                                borderColor: errors.location ? '#d32f2f !important' : '#e2e8f0',
                                            },
                                            height: '38px'
                                        }}
                                        displayEmpty 
                                    >
                                        <MenuItem value="" disabled hidden>Select Location</MenuItem>
                                        {LOCATIONS.map(loc => (
                                            <MenuItem key={loc.value} value={loc.value}>{loc.label}</MenuItem>
                                        ))}
                                    </Select>
                                    {errors.location && <p className="text-[#d32f2f] text-[0.75rem] mt-[3px] mx-[14px] font-normal font-Roboto">{errors.location}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Occasion <span className="text-red-500">*</span></label>
                                <TextField 
                                    size="small" 
                                    type="text" 
                                    name="occasion" 
                                    value={state.occasion} 
                                    placeholder="e.g. New Year's Day" 
                                    onChange={handleChange} 
                                    className="w-full bg-white text-sm" 
                                    error={!!errors.occasion}
                                    helperText={errors.occasion}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            height: '38px',
                                            '& fieldset': {
                                                borderRadius: '0.5rem',
                                                borderColor: errors.occasion ? '#d32f2f !important' : '#e2e8f0',
                                            },
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: state.occasion && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="clear occasion"
                                                    onClick={() => setState({...state, occasion: ''})}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <X size={16} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3.5 flex gap-3 text-blue-700 mt-6 shadow-sm">
                            <div className="mt-0.5 shrink-0">
                                <Info size={18} className="text-blue-500" />
                            </div>
                            <div>
                                <p className="text-[13px] font-medium text-slate-700">Holidays are specific to the selected location.</p>
                                <p className="text-[13px] font-medium text-slate-700 mt-1">Duplicate holidays for the same date and location are not allowed.</p>
                            </div>
                        </div>
                        
                        <div className="pt-8 flex justify-end gap-3 mt-auto">
                            <button 
                                type="button" 
                                onClick={resetForm}
                                className="px-6 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold text-sm"
                            >
                                Clear
                            </button>
                            <button 
                                type="submit" 
                                disabled={disableHoliday} 
                                className={`px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm shadow-sm flex items-center justify-center gap-2 ${disableHoliday ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {disableHoliday ? <LoaderIcon className="w-4 h-4 text-white" /> : null}
                                {state.id ? 'Update Holiday' : 'Save Holiday'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                
                <div className="p-0 flex-1 flex flex-col">
                    <CentralizedTable
                        isLoading={isLoading}
                        className="custom-data-grid"
                        getRowId={row => row.HOLIDAY_ID}
                        rows={filteredHolidays}
                        columns={columns}
                        autoHeight 
                        muiTableBodyRowProps={({ row }) => {
                            const isSelected = selectedDate.isSame(moment(row.original.DATE), 'day');
                            return {
                                onClick: () => {
                                    const day = moment(row.original.DATE);
                                    setSelectedDate(day);
                                    setState(prev => ({
                                        ...prev,
                                        date: day.format("YYYY-MM-DD")
                                    }));
                                },
                                sx: {
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#eff6ff' : undefined,
                                    '&:hover': {
                                        backgroundColor: isSelected ? '#eff6ff' : '#f8fafc',
                                    }
                                }
                            };
                        }}
                        renderTopToolbarCustomActions={() => (
                            <div className="flex items-center h-12">
                                <button
                                    type="button"
                                    onClick={() => setCurrentActiveTab('1')}
                                    className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                        currentActiveTab === '1'
                                        ? 'text-blue-600'
                                        : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                >
                                    India Holidays
                                    {currentActiveTab === '1' && (
                                        <span className="absolute bottom-[-4px] left-0 h-[2px] w-full bg-blue-600" style={{ marginBottom: '-1px' }}></span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentActiveTab('2')}
                                    className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                        currentActiveTab === '2'
                                        ? 'text-blue-600'
                                        : 'text-slate-500 hover:text-slate-700'
                                    }`}
                                >
                                    US Holidays
                                    {currentActiveTab === '2' && (
                                        <span className="absolute bottom-[-4px] left-0 h-[2px] w-full bg-blue-600" style={{ marginBottom: '-1px' }}></span>
                                    )}
                                </button>
                            </div>
                        )}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        hideFooterSelectedRowCount 
                    /> 
                </div>
            </div>
            
            
            <ActionConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setHolidayToDelete(null);
                }}
                onConfirm={deleteHoliday}
                title="Delete Holiday"
                message={`Are you sure you want to delete the holiday "${holidayToDelete?.OCCASION}" on ${moment(holidayToDelete?.DATE).format("DD MMM YYYY")}? This action cannot be undone.`}
                loading={isDeleting}
                confirmText="Delete"
                type="danger"
            />
        </div>
    );
}

export default OthersSA;
